// ═══════════════════════════════════════════════════════════════
// V·IANet Governance — Table of Contents Sidebar
// ═══════════════════════════════════════════════════════════════

import { useState } from 'react';
import { C } from '../../../../../lib/design';
import { DOC_COLORS } from '../../../../../config/documentStyles';
import { useI18n } from '../../../../../i18n';
import type { TocItem } from './renderContent';

export interface TocSidebarProps {
  toc: TocItem[];
  activeTocId: string;
  zoom: number;
  lang: string;
  sidebarOpen: boolean;
  onScrollTo: (id: string) => void;
  onZoomChange: (zoom: number) => void;
  onLangChange: (lang: string) => void;
  onExportPdf?: () => void;
  exportingPdf?: boolean;
}

export function TocSidebar({
  toc,
  activeTocId,
  zoom,
  lang: _lang,
  sidebarOpen,
  onScrollTo,
  onZoomChange,
  onLangChange: _onLangChange,
  onExportPdf,
  exportingPdf = false,
}: TocSidebarProps) {
  const { t } = useI18n();
  const [searchToc, setSearchToc] = useState('');
  const [tocExpanded, setTocExpanded] = useState<Record<string, boolean>>(() => {
    const exp: Record<string, boolean> = {};
    toc.forEach((item) => {
      exp[item.id] = false;
    });
    return exp;
  });

  const filteredToc = searchToc
    ? toc.filter(
        (t) =>
          t.title.toLowerCase().includes(searchToc.toLowerCase()) ||
          t.children.some((c) => c.title.toLowerCase().includes(searchToc.toLowerCase())),
      )
    : toc;

  const searchInputBg = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238B92A5' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cpath d='m21 21-4.35-4.35'/%3E%3C/svg%3E") no-repeat 8px center`;

  const tocItemActiveStyle = {
    borderLeft: '3px solid #00B0F0',
    background: DOC_COLORS.h1Border,
    fontWeight: 600,
    color: C.azul1,
  };
  const tocItemInactiveStyle = { borderLeft: '3px solid transparent', color: C.textSec };

  return (
    <aside
      className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}
      style={{
        width: 300,
        minWidth: 300,
        background: '#f8f9fc',
        borderRight: '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '0 12px 8px', marginTop: 8 }}>
        <input
          type="text"
          placeholder={t.toc.buscarSeccion}
          value={searchToc}
          onChange={(e) => setSearchToc(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 10px 6px 30px',
            border: '1px solid #e2e8f0',
            borderRadius: 5,
            fontSize: 12,
            background: searchInputBg,
            backgroundSize: '14px',
          }}
        />
      </div>

      {/* Zoom + controls row */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '0 12px 4px', gap: 4 }}>
        <button className="sbtn" onClick={() => onZoomChange(Math.max(30, zoom - 10))}>
          −
        </button>
        <span
          style={{
            fontSize: 10,
            color: '#7a7a9a',
            minWidth: 38,
            textAlign: 'center',
            fontWeight: 600,
          }}
        >
          {zoom}%
        </span>
        <button className="sbtn" onClick={() => onZoomChange(Math.min(200, zoom + 10))}>
          +
        </button>
        <div style={{ width: 1, height: 14, background: '#E2E8F0', margin: '0 2px' }} />
        <button
          className="sbtn"
          onClick={() => {
            const exp: Record<string, boolean> = {};
            toc.forEach((i) => {
              exp[i.id] = true;
            });
            setTocExpanded(exp);
          }}
          title="Expandir todo"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <svg
            width={10}
            height={10}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#8B92A5"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        <button
          className="sbtn"
          onClick={() => {
            const exp: Record<string, boolean> = {};
            toc.forEach((i) => {
              exp[i.id] = false;
            });
            setTocExpanded(exp);
          }}
          title="Contraer todo"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <svg
            width={10}
            height={10}
            viewBox="0 0 24 24"
            fill="none"
            stroke="#8B92A5"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 15l-6-6-6 6" />
          </svg>
        </button>
        <div style={{ width: 1, height: 14, background: '#E2E8F0', margin: '0 2px' }} />
        {onExportPdf && (
          <button
            className="sbtn"
            onClick={onExportPdf}
            title="Exportar PDF"
            disabled={exportingPdf}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: exportingPdf ? 0.5 : 1,
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>
        )}
      </div>

      {/* TOC nav */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 12px' }}>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          <li
            onClick={() => onScrollTo('portada')}
            style={{
              display: 'grid',
              gridTemplateColumns: '16px auto 1fr',
              alignItems: 'center',
              gap: 4,
              padding: '6px 8px',
              cursor: 'pointer',
              borderRadius: 4,
              marginBottom: 2,
              fontSize: 12,
              transition: 'all 0.15s',
              ...(activeTocId === 'portada' ? tocItemActiveStyle : tocItemInactiveStyle),
            }}
          >
            <span />
            <span />
            <span>{t.toc.portada}</span>
          </li>

          {filteredToc.map((item) => (
            <li key={item.id} style={{ marginBottom: 2 }}>
              <div
                onClick={() => onScrollTo(item.id)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '16px auto 1fr',
                  alignItems: 'center',
                  gap: 4,
                  padding: '6px 8px',
                  cursor: 'pointer',
                  borderRadius: 4,
                  fontSize: 12,
                  transition: 'all 0.15s',
                  ...(activeTocId === item.id ? tocItemActiveStyle : tocItemInactiveStyle),
                }}
              >
                {item.children.length > 0 ? (
                  <svg
                    width={10}
                    height={10}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#8B92A5"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    onClick={(e) => {
                      e.stopPropagation();
                      setTocExpanded((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
                    }}
                    style={{
                      cursor: 'pointer',
                      flexShrink: 0,
                      transform: tocExpanded[item.id] ? 'rotate(90deg)' : 'rotate(0deg)',
                      transition: 'transform 0.15s',
                    }}
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                ) : (
                  <span />
                )}
                <span style={{ color: DOC_COLORS.h2, fontWeight: 600, fontSize: 12 }}>
                  {item.number}
                </span>
                <span
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {item.title}
                </span>
              </div>

              {item.children.length > 0 && tocExpanded[item.id] && (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, marginLeft: 20 }}>
                  {item.children.map((child) => (
                    <li key={child.id}>
                      <div
                        onClick={() => onScrollTo(child.id)}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '16px auto 1fr',
                          alignItems: 'center',
                          gap: 4,
                          padding: '5px 8px',
                          cursor: 'pointer',
                          borderRadius: 4,
                          fontSize: 12,
                          transition: 'all 0.15s',
                          ...(activeTocId === child.id ? tocItemActiveStyle : tocItemInactiveStyle),
                        }}
                      >
                        <span />
                        <span style={{ color: DOC_COLORS.h2, fontWeight: 600, fontSize: 12 }}>
                          {child.number}
                        </span>
                        <span
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {child.title}
                        </span>
                      </div>
                      {child.children.length > 0 && (
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0, marginLeft: 20 }}>
                          {child.children.map((sub) => (
                            <li
                              key={sub.id}
                              onClick={() => onScrollTo(sub.id)}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '16px auto 1fr',
                                alignItems: 'center',
                                gap: 4,
                                padding: '4px 8px',
                                cursor: 'pointer',
                                borderRadius: 4,
                                fontSize: 11,
                                transition: 'all 0.15s',
                                ...(activeTocId === sub.id
                                  ? tocItemActiveStyle
                                  : tocItemInactiveStyle),
                              }}
                            >
                              <span />
                              <span style={{ color: DOC_COLORS.h3, fontWeight: 600, fontSize: 11 }}>
                                {sub.number}
                              </span>
                              <span
                                style={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {sub.title}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
}
